import { useEffect, useState } from "react";
import { useCheckUsageSourceMutation } from "../../../api/usageCompilationApi";
import {
  convertToSelectableObject,
  getUsageSourceInspectionError,
} from "../utils/usageCompilationFormRules";

const CASCADE_FIELD_NAMES = ["table", "sheet", "consumptionColumn", "subscriptionIdColumn", "timestampColumn", "billingUnitIdColumn", "column"];
const COLUMN_ONLY_FIELD_NAMES = ["consumptionColumn", "subscriptionIdColumn", "timestampColumn", "billingUnitIdColumn"];

/**
 * NEW - no direct precedent in Company/Markets/Billing Units/Usage
 * Sources. Owns the 3-level cascading dependency chain: selecting a
 * usageSource fires `checkUsageSource` (returns per-table/sheet column
 * metadata), selecting a table/sheet from that response then determines
 * the column options for 5 downstream fields. Changing any level clears
 * everything downstream of it.
 *
 * Ported from the old modal's `selectedUsageSource`/`selectedTable`/
 * `selectedSheet` local state + `customHandleChange`'s cascading-clear
 * branches (lines 254-312) + `handleResetCalculation` (lines 376-394).
 *
 * `selectedUsageSource`/`selectedTable`/`selectedSheet` are owned here as
 * plain local state (NOT derived from useDynamicForm's `watch()`) for the
 * same reason Usage Sources' `databaseOptions` had to be lifted out of its
 * validation hook: `checkUsageSource` must fire, and its response must
 * flow into useUsageCompilationFormSchema, *before* useDynamicForm exists
 * (schema is useDynamicForm's input) - so this state can't depend on
 * anything useDynamicForm produces. The three handleXChange functions
 * below take the form's `handleChange` as a parameter instead (called by
 * the state-orchestration layer once useDynamicForm exists).
 */
export function useUsageCompilationCascade({ mode, rowData }) {
  const [
    checkUsageSource,
    { data: sourceData, isLoading: isCheckingSource, error: sourceInspectionError },
  ] = useCheckUsageSourceMutation();

  const [selectedUsageSource, setSelectedUsageSource] = useState(() =>
    mode === "edit" ? rowData?.usageSource || null : null
  );
  const [selectedTable, setSelectedTable] = useState(() =>
    mode === "edit" ? convertToSelectableObject(rowData?.table) : ""
  );
  const [selectedSheet, setSelectedSheet] = useState(() =>
    mode === "edit" ? convertToSelectableObject(rowData?.sheet) : ""
  );

  useEffect(() => {
    // "API LINK" sources have no table/schema to introspect (usage arrives
    // via webhook POST, not a queryable DB) - checking one always resolves
    // to an empty list, which the form then wrongly treated as "still
    // loading options for a required field" instead of "nothing to select
    // here". Same origin check useUsageSourceValidation.js already uses to
    // skip DB validation for this origin (see demo-bugs.md bug #41).
    if (selectedUsageSource?._id && selectedUsageSource?.origin !== "API LINK") {
      checkUsageSource({ _id: selectedUsageSource._id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsageSource?._id, selectedUsageSource?.origin]);

  function handleUsageSourceChange(value, handleChange) {
    const originChanged = value?.origin !== selectedUsageSource?.origin;
    setSelectedUsageSource(value);

    if (originChanged) {
      CASCADE_FIELD_NAMES.forEach((field) => handleChange(field, ""));
      setSelectedTable("");
      setSelectedSheet("");
    }
  }

  function handleTableChange(value, handleChange) {
    setSelectedTable(value);
    COLUMN_ONLY_FIELD_NAMES.forEach((field) => handleChange(field, ""));
  }

  function handleSheetChange(value, handleChange) {
    setSelectedSheet(value);
    handleChange("column", "");
  }

  function resetCalculation(handleChange) {
    handleChange("calculationType", "");
    CASCADE_FIELD_NAMES.forEach((field) => handleChange(field, ""));
    setSelectedTable("");
    setSelectedSheet("");
  }

  return {
    selectedUsageSource,
    selectedTable,
    selectedSheet,
    sourceData,
    isCheckingSource,
    sourceInspectionError: getUsageSourceInspectionError(sourceInspectionError),
    handleUsageSourceChange,
    handleTableChange,
    handleSheetChange,
    resetCalculation,
  };
}
